import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";

const normalize = (value) => String(value ?? "").toLowerCase();

const SearchableDropdown = ({
  items,
  value,
  onValueChange,
  searchValue,
  onSearchValueChange,
  inputPlaceholder = "Ketik untuk mencari...",
  selectedPlaceholder = "Pilih item",
  emptyText = "Data tidak ditemukan",
  disabled = false,
  className,
  inputClassName,
  error,
  helperText,
  selectedLabel = "Dipilih",
  inputTestId,
  listTestId,
  inputRef,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const setMergedInputRef = useCallback(
    (node) => {
      searchInputRef.current = node;
      if (typeof inputRef === "function") {
        inputRef(node);
      } else if (inputRef && typeof inputRef === "object") {
        inputRef.current = node;
      }
    },
    [inputRef]
  );

  const selectedItem = useMemo(
    () => items.find((item) => item.value === value),
    [items, value]
  );

  const visibleItems = useMemo(() => {
    const keyword = normalize(searchValue).trim();
    if (!keyword) {
      return items;
    }
    return items.filter((item) => {
      const haystack = normalize(item.searchText || item.label);
      return haystack.includes(keyword);
    });
  }, [items, searchValue]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      return;
    }

    const firstEnabled = visibleItems.findIndex((item) => !item.disabled);
    setActiveIndex(firstEnabled);
  }, [isOpen, visibleItems]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleFocusIn = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, []);

  const selectItem = (item) => {
    if (!item || item.disabled) {
      return;
    }
    onValueChange(item.value);
    setIsOpen(false);
  };

  const getNextEnabledIndex = (direction) => {
    if (visibleItems.length === 0) {
      return -1;
    }

    let index = activeIndex;
    for (let i = 0; i < visibleItems.length; i += 1) {
      index = (index + direction + visibleItems.length) % visibleItems.length;
      if (!visibleItems[index]?.disabled) {
        return index;
      }
    }
    return -1;
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <input
        type="text"
        value={searchValue}
        onChange={(event) => {
          onSearchValueChange(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          if (!disabled) {
            setIsOpen(true);
          }
        }}
        onKeyDown={(event) => {
          if (disabled) {
            return;
          }

          if (event.key === "ArrowDown") {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex(getNextEnabledIndex(1));
            return;
          }

          if (event.key === "ArrowUp") {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex(getNextEnabledIndex(-1));
            return;
          }

          if (event.key === "Enter" || event.key === "NumpadEnter") {
            event.preventDefault();
            const candidate =
              visibleItems[activeIndex] || visibleItems.find((item) => !item.disabled);
            if (candidate) {
              selectItem(candidate);
              return;
            }
            setIsOpen(true);
            return;
          }

          if (event.key === "Escape") {
            setIsOpen(false);
          }
        }}
        placeholder={inputPlaceholder || selectedPlaceholder}
        disabled={disabled}
        ref={setMergedInputRef}
        data-testid={inputTestId}
        aria-invalid={Boolean(error)}
        className={cn(
          "flex h-11 w-full rounded-xl border border-input bg-white/90 px-11 py-2 text-sm shadow-sm shadow-slate-200/30 ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          error ? "border-destructive/60 bg-red-50/80 focus:ring-destructive" : "",
          inputClassName
        )}
      />
      <Search className="pointer-events-none absolute left-3 top-[14px] h-4 w-4 text-muted-foreground" />
      <ChevronsUpDown className="pointer-events-none absolute right-3 top-[14px] h-4 w-4 text-muted-foreground" />

      {isOpen && !disabled && (
        <div
          data-testid={listTestId}
          className="absolute z-50 mt-2 max-h-64 w-full overflow-y-auto rounded-xl border border-border/80 bg-popover p-1.5 text-popover-foreground shadow-xl shadow-slate-200/60"
        >
          {visibleItems.length === 0 ? (
            <div className="px-3 py-3 text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            visibleItems.map((item) => (
              <button
                key={item.value}
                type="button"
                disabled={item.disabled}
                onMouseEnter={() => {
                  const hoveredIndex = visibleItems.findIndex((x) => x.value === item.value);
                  setActiveIndex(hoveredIndex);
                }}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectItem(item);
                }}
                onClick={() => {
                  selectItem(item);
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" ||
                    event.key === "NumpadEnter" ||
                    event.key === " "
                  ) {
                    event.preventDefault();
                    selectItem(item);
                  }
                }}
                className={cn(
                  "flex w-full items-center rounded-lg px-3 py-2 text-left text-sm",
                  activeIndex >= 0 && visibleItems[activeIndex]?.value === item.value
                    ? "bg-accent text-accent-foreground"
                    : "",
                  item.disabled
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer hover:bg-accent hover:text-accent-foreground"
                )}
              >
                {item.label}
              </button>
            ))
          )}
        </div>
      )}

      {selectedItem ? (
        <div className="mt-2 flex items-start justify-between gap-3 rounded-xl border border-emerald-200/80 bg-emerald-50/70 px-3 py-2.5 shadow-sm shadow-emerald-100/60">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
            <div className="space-y-0.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700/80">
                {selectedLabel}
              </p>
              <p className="text-sm font-medium text-emerald-950">{selectedItem.label}</p>
            </div>
          </div>
          {!disabled ? (
            <button
              type="button"
              className="shrink-0 rounded-lg px-2 py-1 text-xs font-medium text-emerald-800 transition-colors hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
              onClick={() => {
                searchInputRef.current?.focus();
                setIsOpen(true);
              }}
            >
              Ganti
            </button>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p className="mt-2 text-xs leading-5 text-destructive">{error}</p>
      ) : helperText ? (
        <p className="mt-2 text-xs leading-5 text-muted-foreground">{helperText}</p>
      ) : null}
    </div>
  );
};

export default SearchableDropdown;
